import dayjs, { Dayjs } from 'dayjs';

import { cacheInvocation } from './cacheInvocation';

/*
 * Time helper functions to convert data formats
 * */

type DateValue = number | Date | string;

type DateTime = { date: string; time: number };

export class TimeHelper {
  /**
   * Used to ensure Date parses date value in local time.
   * @param dateValue Date value in ISO format (YYYY-MM-DD) or unix timestamp
   * @private
   */
  private static formatLocalDateValue(dateValue: string | number): string | number {
    if (typeof dateValue === 'string') {
      // ISO date-only strings (YYYY-MM-DD) are parsed as UTC. Add time to keep local time.
      return `${dateValue}T00:00:00`;
    }

    return dateValue;
  }

  static toDayjs(date: DateValue, timezone: string): Dayjs {
    return dayjs.tz(date, timezone);
  }

  static toUtcDayjs(date: DateValue): Dayjs {
    return dayjs.utc(date);
  }

  static toStandardFormat(dayjsDate: Dayjs) {
    return dayjsDate.format('YYYY-MM-DD');
  }

  static toDayFormat(dayjsDate: Dayjs) {
    return dayjsDate.format('D');
  }

  static toMonthStringFormat(unix: number, timezone: string) {
    return TimeHelper.toDayjs(unix, timezone).format('MMMM');
  }

  static toMonthAndYearFormat(unix: number, timezone: string) {
    return TimeHelper.toDayjs(unix, timezone).format('MMM YYYY');
  }

  static toWeekStringFormat(unix: number, timezone: string) {
    return TimeHelper.toDayjs(unix, timezone).format('DD.MM.');
  }

  static toWeekOfYearStringFormat(unix: number, timezone: string) {
    return TimeHelper.toDayjs(unix, timezone).format('YYYY-w');
  }

  static toYearAndMonthFormat(unix: number, timezone: string) {
    return TimeHelper.toDayjs(unix, timezone).format('YYYY-MM');
  }

  static convertDateAndTimeToDayjs(obj: { time: number; date: string }, timezone: string) {
    const dateTime = TimeHelper.toDayjs(obj.date, timezone);
    return dateTime.add(obj.time, 'minutes');
  }

  static convertYearAndWeekToDayjs(year: number, week: number, timezone: string) {
    return dayjs.tz(year, timezone).week(week).startOf('week');
  }

  static convertToMinutesFromDayStart(date: Dayjs) {
    return date.hour() * 60 + date.minute();
  }

  static convertUtcHoursToTimezoneHours(obj: { from?: number; to?: number; date?: string }, timezone: string) {
    if (!obj.from || !obj.to) {
      return obj;
    }
    const baseDate = TimeHelper.toUtcDayjs(obj.date || Date.now())
      .startOf('day')
      .valueOf();
    const date = TimeHelper.toDayjs(baseDate, timezone);
    const from = date.clone().add(obj.from, 'minutes');
    const to = date.clone().add(obj.to, 'minutes');
    return {
      ...obj,
      from: TimeHelper.convertToMinutesFromDayStart(from),
      to: TimeHelper.convertToMinutesFromDayStart(to),
    };
  }

  static unixToStandardFormat(unix: number, timezone: string): string {
    return TimeHelper.toStandardFormat(TimeHelper.toDayjs(unix, timezone));
  }

  static toDayAndMonthFormat(unix: number, timezone: string): string {
    return TimeHelper.toDayjs(unix, timezone).format('DD MMM');
  }

  static toDayMonthYearTimeFormat(date: DateValue, timezone: string): string {
    return TimeHelper.toDayjs(date, timezone).format('DD. MMM YYYY, HH:mm');
  }

  static getStartOfHour(unix: number, timezone: string): number {
    return TimeHelper.toDayjs(unix, timezone).startOf('hour').valueOf();
  }

  static getEndOfHour(unix: number, timezone: string): number {
    return TimeHelper.toDayjs(unix, timezone).endOf('hour').valueOf();
  }

  static getStartOfHourLocal(dateValue: number | string): number {
    const dateValueFormatted = TimeHelper.formatLocalDateValue(dateValue);
    const date = new Date(dateValueFormatted);
    date.setMinutes(0, 0, 0);
    return date.getTime();
  }

  static getStartOfDay(unix: number, timezone: string): number {
    return TimeHelper.toDayjs(unix, timezone).startOf('day').valueOf();
  }

  static getEndOfDay(unix: number, timezone: string): number {
    return TimeHelper.toDayjs(unix, timezone).endOf('day').valueOf();
  }

  static getStartOfDayLocal(dateValue: number | string): number {
    const dateValueFormatted = this.formatLocalDateValue(dateValue);
    const date = new Date(dateValueFormatted);
    date.setHours(0, 0, 0, 0);
    return date.getTime();
  }

  static getEndOfDayLocal(dateValue: number | string): number {
    const dateValueFormatted = this.formatLocalDateValue(dateValue);
    const date = new Date(dateValueFormatted);
    date.setHours(23, 59, 59, 999);
    return date.getTime();
  }

  static getStartOfWeek(unix: number, timezone: string): number {
    return TimeHelper.toDayjs(unix, timezone).startOf('week').valueOf();
  }

  static getEndOfWeek(unix: number, timezone: string): number {
    return TimeHelper.toDayjs(unix, timezone).endOf('week').valueOf();
  }

  static getStartOfMonth(unix: number, timezone: string): number {
    return TimeHelper.toDayjs(unix, timezone).startOf('month').valueOf();
  }

  static getEndOfMonth(unix: number, timezone: string): number {
    return TimeHelper.toDayjs(unix, timezone).endOf('month').valueOf();
  }

  static getWeekDaysShort(timezone: string): string[] {
    const now = TimeHelper.toDayjs(Date.now(), timezone);

    const startOfWeek = TimeHelper.getStartOfWeek(now.valueOf(), timezone);

    const days: string[] = [];
    for (let offsetDays = 0; offsetDays < 7; offsetDays += 1) {
      const day = TimeHelper.addDaysTz(startOfWeek, offsetDays, timezone);

      days.push(TimeHelper.toDayjs(day, timezone).format('ddd'));
    }
    return days;
  }

  static setMinutesLocal(unix: number, minutes: number): number {
    const newDate = new Date(unix);
    newDate.setHours(0, minutes, 0, 0);
    return newDate.getTime();
  }

  static addDays(unix: number, days: number, timezone: string): number {
    return TimeHelper.toDayjs(unix, timezone).add(days, 'day').valueOf();
  }

  // safely adds days to date without DST issues
  static addDaysTz(unix: number, days: number, timezone: string): number {
    const dateString = TimeHelper.toStandardFormat(TimeHelper.toDayjs(unix, timezone));
    const date = TimeHelper.toDayjs(dateString, timezone);
    return date.add(days, 'day').valueOf();
  }

  static addWeeks(unix: number, weeks: number, timezone: string): number {
    const dateString = TimeHelper.toStandardFormat(TimeHelper.toDayjs(unix, timezone));
    const date = TimeHelper.toDayjs(dateString, timezone);
    return date.add(weeks, 'week').valueOf();
  }

  static subtractWeeks(unix: number, weeks: number, timezone: string): number {
    const dateString = TimeHelper.toStandardFormat(TimeHelper.toDayjs(unix, timezone));
    const date = TimeHelper.toDayjs(dateString, timezone);
    return date.subtract(weeks, 'week').valueOf();
  }

  static addMonths(unix: number, months: number, timezone: string): number {
    return TimeHelper.toDayjs(unix, timezone).add(months, 'month').valueOf();
  }

  static subtractMonths(unix: number, months: number, timezone: string): number {
    return TimeHelper.toDayjs(unix, timezone).subtract(months, 'month').valueOf();
  }

  static subtractDays(unix: number, days: number, timezone: string): number {
    return TimeHelper.toDayjs(unix, timezone).subtract(days, 'day').valueOf();
  }

  static getDaysDiffUnix(from: number, to: number, timezone: string): number {
    return TimeHelper.toDayjs(to, timezone).diff(TimeHelper.toDayjs(from, timezone), 'days');
  }

  static getTimeByMinutes(_minutes: number) {
    const hours = Math.floor(_minutes / 60);
    const minutes = _minutes % 60;
    const hoursStr = hours < 10 ? `0${hours}` : hours;
    const minutesStr = minutes < 10 ? `0${minutes}` : minutes;
    return `${hoursStr}:${minutesStr}`;
  }

  static getTimeByMinutesWithTimeZone(_minutes: number, timezone: string) {
    const dateBase = TimeHelper.toUtcDayjs(Date.now()).startOf('day').add(_minutes, 'minutes').valueOf();
    return TimeHelper.toDayjs(dateBase, timezone).format('HH:mm');
  }

  static get12HTimeByMinutes(_minutes: number) {
    const hours = Math.floor(_minutes / 60);
    const minutes = _minutes % 60;
    const ampm = hours >= 12 ? 'PM' : 'AM';
    let hoursStr: string | number = hours % 12 || 12; // the hour '0' should be '12'
    hoursStr = hoursStr < 10 ? `0${hoursStr}` : hoursStr;
    const minutesStr = minutes < 10 ? `0${minutes}` : minutes;
    return `${hoursStr}:${minutesStr} ${ampm}`;
  }

  static formatDayOfYear(date: string | number): string {
    // 02. Dezember 2022
    return dayjs(Number(date)).tz().format('DD[.] MMMM YYYY');
  }

  static getTimeByUnix = (date: string | number, with12hours?: boolean) => {
    const time = dayjs(Number(date)).tz();
    if (with12hours) {
      return time.format('hh:mm A');
    }

    return time.format('HH:mm');
  };

  static convertMinutesToMilliseconds(minutes: number) {
    return minutes * 60 * 1000;
  }

  static convertMinutesToSeconds(minutes: number) {
    return minutes * 60;
  }

  static convertMillisecondsToSeconds(milliseconds: number) {
    return milliseconds / 1000;
  }

  static convertSecondsToMilliseconds(seconds: number) {
    return seconds * 1000;
  }

  static convertMillisecondsToMinutes(milliseconds: number) {
    return milliseconds / (60 * 1000);
  }

  static convertUnixTimeToMinutesFromDayStart(unix: number) {
    const startOfDay = dayjs.tz(unix).startOf('day');
    const unixTime = dayjs.tz(unix);

    return unixTime.diff(startOfDay, 'minutes');
  }

  static convertMinutesFromDayStartToMilliseconds(minutes: number) {
    return dayjs.tz(Date.now()).startOf('day').add(minutes, 'minute').valueOf();
  }

  static getCurrentTimeInMilliSeconds() {
    return dayjs.tz(new Date()).utcOffset(0).valueOf();
  }

  static getStartOfDayTimeInMilliSeconds() {
    return dayjs.tz(new Date()).startOf('day').valueOf();
  }

  static getPastMinutesFrom(unix: number) {
    const startTime = dayjs.tz(unix);
    const now = dayjs();

    return now.diff(startTime, 'minutes');
  }

  static getMinutesFromHHmmTimeString = (time: string) => {
    const hours = Number(time.substring(0, 2));
    const minutes = Number(time.substring(3));
    return hours * 60 + minutes;
  };

  public static getHHmmTimeFromMinutes = (value: number) => {
    const hours = Math.floor(value / 60);
    const hoursWithZero = hours < 10 ? `0${hours}` : hours.toString();
    const minutes = Math.floor(value % 60);
    const minutesWithZero = minutes < 10 ? `0${minutes}` : minutes.toString();
    return `${hoursWithZero}:${minutesWithZero}`;
  };

  public static getMinutesFromDayStart(nowDjs: Dayjs = dayjs.tz(Date.now())) {
    return nowDjs.diff(nowDjs.startOf('day'), 'minutes');
  }

  public static getMinutesFromDayStartThrottled = cacheInvocation(this.getMinutesFromDayStart, 1000 * 60 * 1);

  public static getMinutesDiff(from: number, to: number): number {
    return Math.floor(TimeHelper.convertMillisecondsToMinutes(to - from));
  }

  public static getDaysDiff(from: Dayjs, to: Dayjs): number {
    return to.diff(from, 'days');
  }

  public static getWeeksDiff(from: number, to: number, timezone: string): number {
    return TimeHelper.toDayjs(to, timezone).diff(TimeHelper.toDayjs(from, timezone), 'weeks');
  }

  public static getDaysFromTo(from: Dayjs, to: Dayjs): string[] {
    const daysArray: string[] = [];
    const daysBetween = this.getDaysDiff(from, to);
    for (let dayIndex = 0; dayIndex < daysBetween; dayIndex += 1) {
      daysArray.push(from.clone().add(dayIndex, 'days').format('YYYY-MM-DD'));
    }

    return daysArray;
  }

  public static addToDate(date: Date, timezone: string, days: number): Dayjs {
    return this.toDayjs(date, timezone).add(days, 'days');
  }

  public static getCurrentDate(): Date {
    return dayjs.tz(new Date()).toDate();
  }

  public static getIsoWeekRange(date: number) {
    const currentDate = dayjs.tz(date);

    const startOfWeek = currentDate.clone().startOf('isoWeek');
    const endOfWeek = currentDate.clone().endOf('isoWeek');

    return [startOfWeek, endOfWeek];
  }

  public static getWeekdays(date: Dayjs) {
    const days: Dayjs[] = [];
    const weekStart = date.clone().startOf('isoWeek');

    for (let i = 0; i <= 6; i += 1) {
      days.push(dayjs(weekStart).add(i, 'days'));
    }

    return days;
  }

  public static getWeekDay(unix: number): number {
    return new Date(unix).getDay();
  }

  /**
   * Get Dayjs instance with exact date and time to get date with exact time from day start
   *
   * @param dateString - date string in format YYYY-MM-DD
   * @param minutesFromDayStart - minutes from day start
   * @returns Dayjs instance
   */
  public static dateTimeToDayjs(dateString: string, minutesFromDayStart: number = 0): Dayjs {
    return dayjs(dateString).set('minutes', minutesFromDayStart);
  }

  public static timestampToDateAndTimeTz(timestamp: number, timezone: string): DateTime {
    const dateDayjs = TimeHelper.toDayjs(timestamp, timezone);
    const date = TimeHelper.toStandardFormat(dateDayjs);
    const time = TimeHelper.convertToMinutesFromDayStart(dateDayjs);

    return { date, time };
  }

  /**
   *  Converts TZ time to local time
   *  Example: 12:00:00 Europe/Berlin -> 12:00:00 {Local Time}
   */
  public static timestampTzToLocal(timestamp: number, timezone: string): number {
    const dateTime = TimeHelper.timestampToDateAndTimeTz(timestamp, timezone);
    const startOfDayLocal = TimeHelper.getStartOfDayLocal(dateTime.date);
    return TimeHelper.setMinutesLocal(startOfDayLocal, dateTime.time);
  }

  public static dateAndTimeToDateAndTimeTz(dateString: string, minutesFromDayStart: number, timezone: string): DateTime {
    const timestamp = new Date(dateString).valueOf() + minutesFromDayStart * 60 * 1000;

    return TimeHelper.timestampToDateAndTimeTz(timestamp, timezone);
  }

  public static getDateStringTz(date: DateValue, timezone: string) {
    return TimeHelper.toStandardFormat(TimeHelper.toDayjs(date, timezone).startOf('day'));
  }

  public static hoursOffsetBetweenDifferentTimezones(timeZone1: string, timeZone2: string, date?: Date) {
    const tempDate = date ?? new Date();

    const date1 = dayjs(tempDate).tz(timeZone1);
    const date2 = dayjs(tempDate).tz(timeZone2);

    return (date1.utcOffset() - date2.utcOffset()) / 60;
  }

  public static hoursOffsetBetwenLocalAndTimezone(timezone: string, date?: Date) {
    return TimeHelper.hoursOffsetBetweenDifferentTimezones(dayjs.tz.guess(), timezone, date);
  }

  public static getNowDate(timezone: string): Date {
    const dateTimeString = TimeHelper.toDayjs(new Date(), timezone).format('YYYY-MM-DD HH:mm');
    return new Date(dateTimeString);
  }

  public static getNowDateUnix(timezone: string): number {
    return TimeHelper.toDayjs(new Date(), timezone).valueOf();
  }

  public static isBefore(dateBefore: number, date: number, timezone: string) {
    return TimeHelper.toDayjs(dateBefore, timezone).isBefore(TimeHelper.toDayjs(date, timezone));
  }

  public static isBeforeDay(dateBefore: number, date: number, timezone: string) {
    return TimeHelper.toDayjs(dateBefore, timezone).isBefore(TimeHelper.toDayjs(date, timezone), 'day');
  }

  public static setTimeAccordingToMinutesFromDayStartTo(dateTime: Dayjs, min: number) {
    const hours = Math.floor(min / 60);
    const minutes = min % 60;

    return dateTime.hour(hours)
      .minute(minutes)
      .second(0);
  }
}
